import { montilla } from '@quero/montilla-core'
import humps from 'lodash-humps'
import { buildGambitPageAttributes } from '~/assets/js/services/montilla-page-attributes'

export default async function ({ app }, inject) {
  // Expose on window for the direct `window.montilla.track(...)` call sites
  // (lead tracker + per-page typed views like track('content_view', …)) and
  // for tests.
  window.montilla = montilla

  await montilla.load({
    // Public SDK key (client 18) — serves the remote config/mappings,
    // including the `gtm_data_layer` destination.
    clientKey: 'quero_live_31cd14a332fa2bc6e5ed5299426e4799',
    app: {
      name: `@quero/${getCurrentSystem()}`,
      version: '0.0.0',
      environment: montillaEnvironment(),
    },
    page: {
      name: app.context.route.name,
    },
    // autoTrack: captura delegada de cliques/links/forms + section_view.
    // `autoPage:false` — o 3.2.0 liga auto page-view (por pathname) em navegação
    // SPA por padrão; aqui já disparamos `montilla.page(to.name)` no beforeEach
    // abaixo (com o route name), então desligamos o auto pra não duplicar.
    autoTrack: { autoPage: false },
    // Ads: `ads.enabled` vem do client config remoto. O scan de boot roda em
    // `load()`, antes da hidratação, e o SDK não observa o DOM em navegação SPA;
    // cada <MontillaSlot> se pinta ao montar, então o scan automático fica
    // desligado para não pedir o mesmo /ads duas vezes.
    ads: { autoScan: false },
    // Insider (querobolsa). O outer `insider` carrega o ins.js + identidade do
    // partner; `destinations.insider` roteia os eventos montilla pra InsiderQueue.
    // Só resolve nos hosts registrados no rum-api (querobolsa.com.br) — em outros
    // (ex.: *.quero.space) a destination fica inerte.
    insider: {
      partnerName: 'querobolsa',
      partnerId: '10014611',
    },
    destinations: {
      insider: {
        slug: 'insider-default-v1',
      },
    },
  })

  // Route-navigation marker. Typed page-views (`<type>_view`) are fired
  // per-page in each page component (they carry page-specific values).
  //
  // `afterEach` (not `beforeEach`): the SDK reads `window.location` when
  // building `metadata.page.*`, which only points at the new URL once the
  // navigation is confirmed. The attributes feed the
  // `gambit.manual_do_enem.tracking` push of the GTM destination.
  app.router.afterEach((to) => {
    const { base } = app.router.options

    montilla.page(to.name, buildGambitPageAttributes(to, base))
  })

  inject('montilla', montilla)

  if (window._earlyMe) {
    window._earlyMe.then(async (result) => {
      const user = humps(result)
      await montilla.identify(user)
    }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('[montilla] Error identifying user:', error)
    })
  }
}

const getCurrentSystem = () => {
  const fallback = 'manual-do-enem'

  const getFromClient = () => window.__NUXT__?.config?.system?.name
  const getFromServer = () =>
    process.env?.SYSTEM_NAME ?? process.env?.systemName ?? fallback

  if (process?.client) {
    return getFromClient() ?? getFromServer()
  } else if (process?.server) {
    return getFromServer()
  }

  return fallback
}

// Montilla builds its collector/mappings host from this Environment
// (`production` | `homolog` | `development`). The deploy env isn't reliably
// exposed to the client bundle and the baked `process.env.NODE_ENV` isn't one
// of montilla's keys — either would leave the rum host as the literal string
// `undefined`. The reliable client-side signal is the hostname.
const montillaEnvironment = () => {
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  if (/localhost|127\.0\.0\.1/.test(host)) return 'development'
  if (host.includes('homolog')) return 'homolog'
  return 'production'
}
